/**
 * SUNTRONIX ONE — ONE Risk Intelligence.
 *
 * Combines NVD + CISA KEV + FIRST EPSS + OSV.dev (Phase 1) into a
 * priority-ordered list.  Every row shows WHY it is prioritised (§7)
 * — no unexplained AI risk score.  Rows are labelled
 * POTENTIALLY_AFFECTED until asset-match confidence is applied (§6).
 */
import { useEffect, useState, useCallback } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import {
  ShieldAlert, ShieldCheck, Zap, TrendingUp, ExternalLink,
  Loader2, Info, Cable,
} from "lucide-react";

const SEV_TONE = {
  CRITICAL: "bg-[#FFF0F2] text-[#C2333F]",
  HIGH:     "bg-[#FFF8E7] text-[#B25A00]",
  MEDIUM:   "bg-[#EFF5FF] text-[#246BFD]",
  LOW:      "bg-[#F2F4F7] text-[#667085]",
};

export default function RiskIntelligence() {
  const [summary, setSummary] = useState(null);
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(false);
  const [onlyKev, setOnlyKev] = useState(false);
  const [minEpss, setMinEpss] = useState(0);

  const load = useCallback(async () => {
    setBusy(true);
    try {
      const [s, r] = await Promise.all([
        api.get("/insights/security/risk-intelligence/summary"),
        api.get("/insights/security/risk-intelligence", {
          params: { limit: 50, only_kev: onlyKev, min_epss: minEpss },
        }),
      ]);
      setSummary(s.data);
      setItems(r.data.items || []);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  }, [onlyKev, minEpss]);
  useEffect(() => { load(); }, [load]);

  return (
    <div className="max-w-[1400px] space-y-4" data-testid="risk-intel-page">
      <div>
        <div className="text-[10px] font-semibold tracking-[0.08em] uppercase text-[#667085]">
          Insights · Technology security
        </div>
        <h1 className="text-2xl font-heading font-semibold text-[#101828] tracking-tight">
          ONE Risk Intelligence
        </h1>
        <p className="text-[13px] text-[#667085] mt-1 max-w-3xl">
          NVD + CISA KEV + FIRST EPSS + OSV.dev · combined into a
          transparent priority list.  Rows are <b>potentially affected</b>
          until asset-match confidence is applied — <b>absence of a
          CVE here does not prove safety</b>.
        </p>
      </div>

      {/* Summary strip */}
      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <Kpi label="Known exploited (KEV)" value={summary.kev_known_exploited}
                tone="coral" icon={<ShieldAlert className="h-4 w-4" />} />
          <Kpi label="High EPSS ≥ 0.5"     value={summary.epss_high_probability}
                tone="amber" icon={<TrendingUp className="h-4 w-4" />} />
          <Kpi label="Critical CVSS"         value={summary.critical_severity}
                tone="coral" icon={<Zap className="h-4 w-4" />} />
          <Kpi label="NVD CVEs cached"       value={summary.nvd_total_cves}
                tone="blue" icon={<Cable className="h-4 w-4" />} />
          <Kpi label="OSV findings"          value={summary.osv_findings}
                tone="blue" icon={<ShieldCheck className="h-4 w-4" />} />
        </div>
      )}

      {/* Filters */}
      <div className="flex items-center gap-3 flex-wrap bg-white border border-slate-200 rounded-md p-3">
        <label className="flex items-center gap-2 text-[12px] text-[#101828]">
          <input type="checkbox" checked={onlyKev} onChange={(e) => setOnlyKev(e.target.checked)}
                    data-testid="risk-only-kev" />
          Known Exploited only
        </label>
        <label className="flex items-center gap-2 text-[12px] text-[#101828]">
          Min EPSS
          <Input type="number" step="0.1" min="0" max="1"
                  className="h-8 w-20"
                  value={minEpss}
                  onChange={(e) => setMinEpss(parseFloat(e.target.value) || 0)}
                  data-testid="risk-min-epss" />
        </label>
        <div className="ml-auto text-[11px] text-[#667085] flex items-center gap-1">
          <Info className="h-3 w-3" />
          Sources: NVD · CISA KEV · FIRST EPSS · OSV
        </div>
      </div>

      {/* Table */}
      <div className="bg-white border border-slate-200 rounded-md overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-[10px] uppercase text-[#667085]">
            <tr>
              <th className="text-left px-3 py-2 w-[140px]">CVE</th>
              <th className="text-left px-3 py-2">Summary</th>
              <th className="text-left px-3 py-2">Severity</th>
              <th className="text-left px-3 py-2">EPSS</th>
              <th className="text-left px-3 py-2">KEV</th>
              <th className="text-left px-3 py-2">Priority</th>
              <th className="text-left px-3 py-2">Status</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {busy && items.length === 0 ? (
              <tr><td colSpan={7} className="px-3 py-6 text-center text-[#98A2B3]">
                <Loader2 className="h-4 w-4 mx-auto animate-spin" />
              </td></tr>
            ) : items.length === 0 ? (
              <tr><td colSpan={7} className="px-3 py-8 text-center text-[#98A2B3] text-[13px]">
                <ShieldCheck className="h-5 w-5 mx-auto mb-2 opacity-50 text-[#169C70]" />
                No security intelligence loaded yet.  Sync the free connectors
                from Administration → ONE CONNECT → Free &amp; open connectors.
              </td></tr>
            ) : items.map((i) => (
              <tr key={i.cve_id} className="hover:bg-slate-50"
                  data-testid={`risk-row-${i.cve_id}`}>
                <td className="px-3 py-2">
                  <a href={`https://nvd.nist.gov/vuln/detail/${i.cve_id}`}
                      target="_blank" rel="noreferrer"
                      className="text-[12px] font-mono text-[#246BFD] hover:underline flex items-center gap-1">
                    {i.cve_id} <ExternalLink className="h-3 w-3" />
                  </a>
                  <div className="text-[10px] text-[#667085] mt-0.5">
                    {i.sources?.join(" · ")}
                  </div>
                </td>
                <td className="px-3 py-2 text-[12px] text-[#101828] max-w-md">
                  <div className="line-clamp-2">{i.description || "—"}</div>
                  {i.cwes?.length > 0 && (
                    <div className="text-[10px] text-[#667085] mt-1">
                      {i.cwes.slice(0, 3).join(" · ")}
                    </div>
                  )}
                </td>
                <td className="px-3 py-2">
                  {i.severity ? (
                    <span className={`inline-flex rounded-full px-2 py-0.5 text-[10px] font-semibold ${SEV_TONE[i.severity] || SEV_TONE.LOW}`}>
                      {i.severity} · {i.cvss_score ?? "—"}
                    </span>
                  ) : <span className="text-[11px] text-[#98A2B3]">—</span>}
                </td>
                <td className="px-3 py-2 text-[12px] tabular-nums">
                  {i.epss !== null && i.epss !== undefined
                    ? `${Math.round(i.epss * 100)}%`
                    : <span className="text-[#98A2B3]">—</span>}
                </td>
                <td className="px-3 py-2">
                  {i.known_exploited ? (
                    <span className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold bg-[#FFF0F2] text-[#C2333F]">
                      <ShieldAlert className="h-3 w-3" /> KEV
                    </span>
                  ) : <span className="text-[11px] text-[#98A2B3]">—</span>}
                </td>
                <td className="px-3 py-2">
                  <div className="text-[13px] font-semibold text-[#101828] tabular-nums">{i.priority_score}</div>
                  <div className="text-[10px] text-[#667085] leading-relaxed max-w-[220px]">
                    {(i.priority_reasons || []).join(" · ")}
                  </div>
                </td>
                <td className="px-3 py-2">
                  <span className="inline-flex rounded-full px-2 py-0.5 text-[10px] font-semibold bg-[#F2F4F7] text-[#667085]">
                    {(i.status || "").replace(/_/g, " ")}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="text-[11px] text-[#667085] leading-relaxed bg-slate-50 border border-slate-200 rounded p-3">
        <b>Method:</b> Priority = 500·KEV + 250·Ransomware + 300·EPSS + 10·CVSS +
        severity boost.  Every reason is shown per row per PRD §7.
        Rows here are cached copies — dashboards never call NVD / KEV /
        EPSS / OSV live to respect provider rate limits.
      </div>
    </div>
  );
}

function Kpi({ label, value, tone, icon }) {
  const map = {
    blue:  { bg: "#EFF5FF", accent: "#246BFD" },
    coral: { bg: "#FFF0F2", accent: "#C2333F" },
    amber: { bg: "#FFF8E7", accent: "#B25A00" },
    mint:  { bg: "#ECFDF6", accent: "#169C70" },
  };
  const t = map[tone] || map.blue;
  return (
    <div className="rounded-md border border-slate-200 p-3 relative overflow-hidden"
            style={{ background: `linear-gradient(180deg, ${t.bg} 0%, #FFFFFF 60%)` }}>
      <div className="absolute inset-y-0 left-0 w-1" style={{ background: t.accent }} />
      <div className="flex items-center justify-between">
        <div className="text-[10px] uppercase tracking-[0.14em] text-[#667085]">{label}</div>
        <span style={{ color: t.accent }}>{icon}</span>
      </div>
      <div className="mt-1 text-2xl font-semibold text-[#101828] tabular-nums">
        {(value ?? 0).toLocaleString()}
      </div>
    </div>
  );
}
